import Box from 'assets/components/box/Box';
import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { HttpStatus } from 'config/Api.Config';
import { isEmpty } from 'lodash';
import claimTypeDealerGroupAutomationRuleDeleteApi from 'models/dealerManagement/claimTypeDealerGroupAutomationRule/delete/Api.ClaimTypeDealerGroupAutomationRule.Delete';
import AutomationRulesTable from 'module/automation/components/Automation.Rule.Table';
import { createRule } from 'module/automation/helpers/ruleHelpers';
import { createDealerGroupClaimAutomationSchema } from 'module/automation/schemas/ClaimAutomationSchema';
import useClaimStatuses from 'module/claims/hooks/useClaimStatuses';
import React, { Fragment, useEffect, useState } from 'react';
import { useDealerGroupProvider } from '../../DealerGroup.Hooks';
import { DealerGroupForm } from '../DealerGroup.Edit';

const DealerGroupClaimTypeAutomationRules: React.FC = () => {
  const { lang } = getLocales();
  const { form } = useModalForm<DealerGroupForm>();
  const { claimTypesPayload } = useDealerGroupProvider();
  const { automationClaimStatuses, automationClaimJobStatuses } = useClaimStatuses();
  const currentRules = form.data?.claimTypeDealerGroupAutomationRules ?? [];

  const createOverrideRuleData = (
    rules: Model.IClaimTypeDealerGroupAutomationRule[],
    rule: Model.IClaimTypeAutomationRule,
    claimTypeId: number
  ) => ({
    id: Math.min(0, ...rules.map((item) => item.id || 0)) - 1,
    claimTypeId,
    claimTypeAutomationRuleId: rule.id,
  });

  const updateRules = (
    id: number,
    newRules: Model.IClaimTypeDealerGroupAutomationRule[],
    claimTypeId: number
  ) => {
    const otherRules = currentRules.filter((entry) => entry.claimTypeAutomationRuleId !== id);
    const updatedRules = newRules.map((entry) => ({
      ...entry,
      claimTypeId,
      claimTypeAutomationRuleId: id,
    }));

    form.update({ claimTypeDealerGroupAutomationRules: [...otherRules, ...updatedRules] });
  };

  const updateEnabled = (claimTypeId: number, newData: { isClaimTypeAutomationEnabled: any }) => {
    const currentDealerClaimTypes = [...(form.data.dealerGroupClaimTypes || [])];
    const updated = currentDealerClaimTypes.map((entry) =>
      entry.claimTypeId === claimTypeId ? { ...entry, ...newData } : entry
    );
    form.update({ dealerGroupClaimTypes: updated });
  };

  const deleteRule = async (claimTypeRuleId: number, rule?: Model.IClaimTypeDealerGroupAutomationRule) => {
    const updatedRules = form.data?.claimTypeDealerGroupAutomationRules?.filter(
      (it) => it.claimTypeAutomationRuleId !== claimTypeRuleId || it.id !== rule?.id
    );
    form.update({ claimTypeDealerGroupAutomationRules: updatedRules });

    if (!updatedRules?.some((it) => it.claimTypeAutomationRuleId === claimTypeRuleId)) {
      setOverrideClaimRule((prev) => ({
        ...prev,
        [claimTypeRuleId]: false,
      }));
    }

    if (rule?.id && rule.id > 0) {
      const res = await claimTypeDealerGroupAutomationRuleDeleteApi({ id: rule.id });
      if (res.status !== HttpStatus.ok) {
        addLog({ error: res.message });
      }
    }
  };

  const deleteOverrideRules = async (claimTypeRuleId: number) => {
    const deletedRules = currentRules.filter((it) => it.claimTypeAutomationRuleId === claimTypeRuleId);
    const updatedRules = currentRules.filter((it) => it.claimTypeAutomationRuleId !== claimTypeRuleId);
    form.update({ claimTypeDealerGroupAutomationRules: updatedRules });

    setOverrideClaimRule((prev) => ({
      ...prev,
      [claimTypeRuleId]: false,
    }));

    for (const deletedRule of deletedRules) {
      if (deletedRule.id && deletedRule.id > 0) {
        const res = await claimTypeDealerGroupAutomationRuleDeleteApi({ id: deletedRule.id });
        if (res.status !== HttpStatus.ok) {
          addLog({ error: res.message });
        }
      }
    }
  };

  const [overrideClaimRule, setOverrideClaimRule] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isEmpty(overrideClaimRule)) {
      const initialOverrides: Record<string, boolean> = {};

      claimTypesPayload?.data?.forEach((claimType) => {
        const automationRuleIds = claimType.claimTypeAutomationRules?.map((r) => r.id) ?? [];
        const hasDealerOverride = (form.data?.claimTypeDealerGroupAutomationRules ?? []).some((dealerRule) =>
          automationRuleIds.includes(dealerRule.claimTypeAutomationRuleId)
        );

        if (hasDealerOverride) {
          automationRuleIds.forEach((ruleId) => {
            if (
              (form.data?.claimTypeDealerGroupAutomationRules ?? []).some(
                (dealerRule) => dealerRule.claimTypeAutomationRuleId === ruleId
              )
            ) {
              initialOverrides[ruleId] = true;
            }
          });
        }
      });

      setOverrideClaimRule(initialOverrides);
    }
  }, [claimTypesPayload, form.data?.claimTypeDealerGroupAutomationRules]);

  return (
    <FormWrapper>
      <DataMap
        data={claimTypesPayload?.data}
        render={({ data: claimType }) => {
          const dealerGroupClaimType = form.data?.dealerGroupClaimTypes?.find((it) => it.claimTypeId === claimType.id);
          const claimTypeSchema = createDealerGroupClaimAutomationSchema({
            claimStatuses: automationClaimStatuses,
            claimJobStatuses: automationClaimJobStatuses,
            claimTypeDataPoints: claimType.claimTypeDataPoints,
          });

          return (
            !!claimType.claimTypeAutomationRules.length &&
            dealerGroupClaimType && (
              <Fragment key={claimType.id}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: Theme.Size.M }}>
                    <h3>{claimType.name}</h3>
                    <CheckWrapper>
                      <CheckInput
                        name="isClaimTypeAutomationEnabled"
                        label={lang.enabled}
                        value={dealerGroupClaimType?.isClaimTypeAutomationEnabled}
                        onChange={({ isClaimTypeAutomationEnabled }) =>
                          updateEnabled(claimType.id, { isClaimTypeAutomationEnabled })
                        }
                        class={inputStyles.check.switch}
                        htmlElementProps={{
                          title: dealerGroupClaimType?.isClaimTypeAutomationEnabled ? lang.disable : lang.enable,
                        }}
                      />
                    </CheckWrapper>
                  </div>
                </div>
                &nbsp;
                {dealerGroupClaimType?.isClaimTypeAutomationEnabled && (
                  <DataMap
                    data={claimType.claimTypeAutomationRules}
                    render={({ data: rule, index }) => {
                      const overrideRules = currentRules.filter((c) => c.claimTypeAutomationRuleId === rule.id);

                      return (
                        <div key={rule.id}>
                          <Box style={{ display: 'flex', flexDirection: 'column', marginBottom: '1rem' }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: Theme.Size.M,
                                marginBottom: '10px',
                              }}
                            >
                              <h3>{`${lang.claimRule} #${index + 1}`}</h3>
                              <CheckWrapper>
                                <CheckInput
                                  name="overrideClaimRule"
                                  label={lang.overrideClaimRule}
                                  value={overrideClaimRule[rule.id]}
                                  onChange={({ overrideClaimRule: isOverrided }) => {
                                    setOverrideClaimRule((prev) => ({ ...prev, [rule.id]: isOverrided }));
                                    const allOverrideRules = form.data?.claimTypeDealerGroupAutomationRules || [];

                                    if (isOverrided) {
                                      const alreadyExists = allOverrideRules.some(
                                        (it) => it.claimTypeAutomationRuleId === rule.id
                                      );
                                      if (alreadyExists) return;

                                      const newEntry = createRule(
                                        claimTypeSchema,
                                        createOverrideRuleData([], rule, claimType.id)
                                      ) as Model.IClaimTypeDealerGroupAutomationRule;

                                      form.update({
                                        claimTypeDealerGroupAutomationRules: [...allOverrideRules, newEntry],
                                      });
                                    } else {
                                      deleteOverrideRules(rule.id);
                                    }
                                  }}
                                  class={inputStyles.check.radio}
                                />
                              </CheckWrapper>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: Theme.Size.M }}>
                              <AutomationRulesTable<Model.IClaimTypeAutomationRule>
                                value={[rule]}
                                schema={claimTypeSchema}
                                isReadOnlySummary
                              />
                            </div>
                          </Box>
                          {overrideClaimRule[rule.id] && (
                            <div>
                              <h3>{`${lang.overrideClaimRule} #${index + 1}`}</h3>
                              <AutomationRulesTable<Model.IClaimTypeDealerGroupAutomationRule>
                                value={overrideRules as Model.IClaimTypeDealerGroupAutomationRule[]}
                                schema={claimTypeSchema}
                                createRuleData={(rules) => createOverrideRuleData(rules, rule, claimType.id)}
                                onChange={(rules) => updateRules(rule.id, rules, claimType.id)}
                                onDeleteRule={(_, overrideRule) => deleteRule(rule.id, overrideRule)}
                              />
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                )}
                <br />
              </Fragment>
            )
          );
        }}
      />
    </FormWrapper>
  );
};

export default DealerGroupClaimTypeAutomationRules;
